import { useEffect } from 'react';

export default function SuperbuySpreadsheetShoesGuide() {
  useEffect(() => {
    window.scrollTo(0, 0);

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'Article',
      'headline': 'Superbuy Spreadsheet Shoes Guide: How to Find Your Perfect Pair Through Organized Discovery',
      'description': 'A comprehensive guide to the Shoes category in the Superbuy Spreadsheet — covering sneakers, boots, formal shoes, seasonal footwear, and advanced tips for finding rare and trending pairs through structured product discovery.',
      'author': {
        '@type': 'Person',
        'name': 'Alex Carter',
        'jobTitle': 'Product Research Analyst'
      },
      'datePublished': '2026-07-13',
      'dateModified': '2026-07-13',
      'url': 'https://superbuyspread.ink/superbuy-spreadsheet-shoes-guide',
      'publisher': {
        '@type': 'Organization',
        'name': 'SuperBuySpread'
      },
      'mainEntityOfPage': {
        '@type': 'WebPage',
        '@id': 'https://superbuyspread.ink/superbuy-spreadsheet-shoes-guide'
      }
    });
    document.head.appendChild(script);

    const faqScript = document.createElement('script');
    faqScript.type = 'application/ld+json';
    faqScript.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      'mainEntity': [
        {
          '@type': 'Question',
          'name': 'What types of shoes can I find in the Superbuy Spreadsheet Shoes category?',
          'acceptedAnswer': {
            '@type': 'Answer',
            'text': 'The Shoes category spans sneakers, formal leather shoes, casual loafers, boots, sandals, athletic trainers, seasonal footwear, and limited-edition collaborations. The collection is refreshed weekly with new product links and trending styles from curated sources.'
          }
        },
        {
          '@type': 'Question',
          'name': 'How does the Superbuy Spreadsheet help with sneaker discovery?',
          'acceptedAnswer': {
            '@type': 'Answer',
            'text': 'Unlike random browsing, the spreadsheet organizes sneaker links by style, brand, and release window, so collectors can jump directly to relevant product pages without navigating multiple fragmented platforms. Weekly updates ensure new drops appear faster than most social feeds.'
          }
        },
        {
          '@type': 'Question',
          'name': 'Can I use the Shoes category for budget-friendly footwear shopping?',
          'acceptedAnswer': {
            '@type': 'Answer',
            'text': 'Yes. The Shoes category includes products across price ranges, from affordable everyday sneakers to designer collaborations. The organized structure helps budget shoppers compare options efficiently without getting lost in algorithmic product feeds.'
          }
        },
        {
          '@type': 'Question',
          'name': 'How often does the Superbuy Spreadsheet update shoes and footwear links?',
          'acceptedAnswer': {
            '@type': 'Answer',
            'text': 'The Shoes category is updated weekly alongside the rest of the spreadsheet. As the most popular and frequently-accessed category, shoe links receive priority verification to ensure availability and freshness.'
          }
        },
        {
          '@type': 'Question',
          'name': 'What makes the Superbuy Spreadsheet different from searching shoes on a marketplace?',
          'acceptedAnswer': {
            '@type': 'Answer',
            'text': 'Marketplace searches rely on algorithms that prioritize promoted listings and paid placements. The Superbuy Spreadsheet uses human-curated, category-organized links that reflect genuine product variety — no ad bias, no hidden sponsorship. Each link goes directly to the product collection on the main platform.'
          }
        }
      ]
    });
    document.head.appendChild(faqScript);

    return () => {
      document.head.removeChild(script);
      document.head.removeChild(faqScript);
    };
  }, []);

  return (
    <main>
      {/* Navbar */}
      <nav className="sticky top-0 z-50 bg-background-50/95 backdrop-blur-md border-b border-background-200/60">
        <div className="flex items-center justify-between px-6 md:px-12 py-4 max-w-7xl mx-auto w-full">
          <a href="/" className="flex items-center gap-3 cursor-pointer">
            <div className="w-9 h-9 rounded-full bg-primary-500 flex items-center justify-center">
              <i className="ri-store-2-line text-background-50 text-base"></i>
            </div>
            <span className="font-heading text-lg font-semibold text-primary-800 whitespace-nowrap">SuperBuySpread</span>
          </a>
          <div className="flex items-center gap-6">
            <a href="/superbuy-spreadsheet-guide" className="text-sm font-medium text-foreground-600 hover:text-foreground-950 transition-colors whitespace-nowrap">
              Complete Guide
            </a>
            <a href="/" className="text-sm font-medium text-foreground-600 hover:text-foreground-950 transition-colors whitespace-nowrap">
              <i className="ri-arrow-left-line mr-1"></i> Home
            </a>
          </div>
        </div>
      </nav>

      {/* Article Header with Hero Image */}
      <section className="relative py-20 md:py-28 px-6 md:px-12">
        <div className="absolute inset-0 z-0">
          <img
            src="https://readdy.ai/api/search-image?query=Artistic%20overhead%20flat%20lay%20of%20diverse%20premium%20sneakers%20and%20leather%20shoes%20arranged%20in%20a%20circular%20composition%20on%20warm%20cream%20linen%20surface%2C%20natural%20diffused%20window%20light%20creating%20soft%20shadows%2C%20editorial%20fashion%20photography%20with%20organic%20styling%2C%20warm%20neutral%20tones%20with%20subtle%20sage%20green%20accents%2C%20high%20detail%20product%20arrangement%20with%20intentional%20negative%20space&width=1600&height=700&seq=shoes-guide-hero-x2&orientation=landscape"
            alt="Superbuy Spreadsheet Shoes category curated footwear collection"
            title="Superbuy Spreadsheet - Shoes Guide"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background-50/60 via-background-50/35 to-background-50/95"></div>
        </div>
        <div className="relative z-10 max-w-3xl mx-auto text-center">
          <div className="flex items-center justify-center gap-3 mb-6">
            <span className="text-xs font-medium tracking-widest uppercase text-accent-600 bg-accent-100/60 px-3 py-1 rounded-full whitespace-nowrap">Category Deep Dive</span>
            <span className="text-xs text-foreground-400">Updated July 2026</span>
          </div>
          <h1 className="font-heading text-3xl md:text-5xl font-bold text-foreground-950 leading-tight">
            The Ultimate Superbuy Spreadsheet Shoes Guide
          </h1>
          <p className="text-sm md:text-base text-foreground-500 mt-4 max-w-lg mx-auto leading-relaxed">
            From limited-edition sneakers to timeless leather classics — master the Shoes category and transform how you discover footwear online.
          </p>
          <div className="flex items-center justify-center gap-4 mt-8">
            <div className="w-10 h-10 rounded-full bg-secondary-200 flex items-center justify-center">
              <span className="text-sm font-semibold text-secondary-700">AC</span>
            </div>
            <div className="text-left">
              <p className="text-sm font-semibold text-foreground-900">Alex Carter</p>
              <p className="text-xs text-foreground-500">Product Research Analyst</p>
            </div>
            <span className="text-xs text-foreground-400 ml-4">6 min read</span>
          </div>
        </div>
      </section>

      {/* Article Body */}
      <article className="py-12 md:py-16 px-6 md:px-12 bg-background-50">
        <div className="max-w-3xl mx-auto">
          {/* Direct Answer Box */}
          <div className="bg-background-100 border border-background-200/60 rounded-2xl p-6 mb-10">
            <p className="text-xs font-semibold tracking-widest uppercase text-secondary-600 mb-2">Quick Answer</p>
            <p className="text-sm font-semibold text-foreground-900 mb-1">What makes the Shoes category the most important section of the Superbuy Spreadsheet?</p>
            <p className="text-sm text-foreground-600 leading-relaxed">
              Shoes are the anchor of any wardrobe — and the anchor of the Superbuy Spreadsheet. This category consistently contains the most product links, the fastest update cycles, and the widest variety spanning casual sneakers, dress shoes, seasonal boots, athletic footwear, and limited collaborations. As the most frequently accessed section, the Shoes category receives priority curation to ensure discoverability stays sharp.
            </p>
          </div>

          {/* Section 1 */}
          <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-950 mt-12 mb-5">
            Why Shoes Deserve Their Own Deep Dive
          </h2>
          <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
            Footwear is not just another fashion category — it is often the first piece shoppers decide on when building an outfit. The right pair of shoes anchors everything else: the jacket, the bag, even the accessories. When the Superbuy Spreadsheet was originally designed, the Shoes category was intentionally positioned as the starting point for product discovery because shoes drive the most search volume and conversion interest across the entire platform.
          </p>
          <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
            The <strong>Superbuy Spreadsheet Shoes category</strong> is structured differently from the other 13 sections. Rather than grouping all shoe links into a single undifferentiated list, the category is internally organized by silhouette type, release recency, and seasonal relevance. This means whether you are hunting for vintage Nike Air Max, formal Oxfords for a wedding, or the newest Yeezy drop, the spreadsheet routes you directly to the right product page without detours.
          </p>

          {/* Image break */}
          <div className="my-10 rounded-2xl overflow-hidden aspect-[16/7]">
            <img
              src="https://readdy.ai/api/search-image?query=Beautiful%20flat%20lay%20of%20four%20different%20shoe%20styles%20on%20warm%20textured%20surface%2C%20white%20leather%20sneakers%20next%20to%20brown%20leather%20loafers%20and%20suede%20desert%20boots%2C%20soft%20natural%20window%20light%20from%20upper%20left%2C%20editorial%20product%20photography%20with%20warm%20terracotta%20and%20cream%20tones%2C%20organic%20styling%20with%20linen%20fabric%20backdrop%20accents&width=1200&height=525&seq=shoes-guide-mid-x2&orientation=landscape"
              alt="Superbuy Spreadsheet diverse shoe styles organized for discovery"
              title="Superbuy Spreadsheet - Shoes Category Overview"
              className="w-full h-full object-cover"
            />
          </div>

          {/* Section 2 */}
          <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-950 mt-12 mb-5">
            What Kinds of Shoes Are Actually in the Spreadsheet
          </h2>
          <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
            The Shoes category is deliberately broad because footwear tastes are incredibly diverse. Here is a realistic breakdown of what you can expect to find every week:
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
            {[
              { type: 'Sneakers', desc: 'The largest sub-category. Covers retro runners, basketball silhouettes, skate shoes, chunky dad sneakers, minimalist leather trainers, and high-top classics from major and boutique brands.' },
              { type: 'Formal & Dress Shoes', desc: 'Oxfords, Derbies, monk straps, brogues, and loafers in leather and suede. Ideal for professionals, wedding guests, and anyone refreshing their smart-casual rotation.' },
              { type: 'Boots', desc: 'Chelsea boots, combat boots, desert boots, and winter hikers. The cold-weather selection expands significantly during autumn and winter update cycles.' },
              { type: 'Sandals & Slides', desc: 'Summer essentials including leather sandals, sport slides, espadrilles, and pool-ready flip-flops. Peaks in May through August but available year-round.' },
              { type: 'Athletic & Performance', desc: 'Running shoes, training sneakers, court shoes, and specialized sports footwear. These links often include technical specifications in the product descriptions.' },
              { type: 'Limited & Collaborative', desc: 'The treasure-hunt section. Collaborative releases, anniversary editions, and region-exclusive colorways that appear in short update windows before selling through.' },
            ].map((item) => (
              <div key={item.type} className="bg-background-100 rounded-xl p-4 border border-background-200/60">
                <h3 className="font-semibold text-foreground-900 text-sm mb-1">{item.type}</h3>
                <p className="text-xs text-foreground-500 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>

          {/* Comparison Table */}
          <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-950 mt-12 mb-5">
            Shoes Category vs Manual Sneaker Searching
          </h2>
          <div className="overflow-x-auto mb-6">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="border-b-2 border-background-200">
                  <th className="text-left py-3 px-4 font-semibold text-foreground-900">Aspect</th>
                  <th className="text-left py-3 px-4 font-semibold text-foreground-900">Superbuy Spreadsheet Shoes</th>
                  <th className="text-left py-3 px-4 font-semibold text-foreground-900">Browsing Marketplaces</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-background-200/60">
                  <td className="py-3 px-4 text-foreground-700 font-medium">Organization</td>
                  <td className="py-3 px-4 text-secondary-700">Categorized by silhouette</td>
                  <td className="py-3 px-4 text-foreground-500">Algorithmic feed, mixed relevance</td>
                </tr>
                <tr className="border-b border-background-200/60">
                  <td className="py-3 px-4 text-foreground-700 font-medium">New Drops</td>
                  <td className="py-3 px-4 text-secondary-700">Weekly priority refresh</td>
                  <td className="py-3 px-4 text-foreground-500">Depends on seller upload timing</td>
                </tr>
                <tr className="border-b border-background-200/60">
                  <td className="py-3 px-4 text-foreground-700 font-medium">Bias</td>
                  <td className="py-3 px-4 text-secondary-700">No ad or sponsorship bias</td>
                  <td className="py-3 px-4 text-foreground-500">Promoted listings dominate</td>
                </tr>
                <tr className="border-b border-background-200/60">
                  <td className="py-3 px-4 text-foreground-700 font-medium">Brand Variety</td>
                  <td className="py-3 px-4 text-secondary-700">Mixed — boutique to mainstream</td>
                  <td className="py-3 px-4 text-foreground-500">This is mostly major sellers</td>
                </tr>
                <tr>
                  <td className="py-3 px-4 text-foreground-700 font-medium">Time to Discover</td>
                  <td className="py-3 px-4 text-secondary-700">Minutes — direct links</td>
                  <td className="py-3 px-4 text-foreground-500">Can take hours of scrolling</td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Decision Layer */}
          <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-950 mt-12 mb-5">
            Who Gets the Most Out of the Shoes Category
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <div className="bg-secondary-50 border border-secondary-200/60 rounded-2xl p-6">
              <h3 className="font-heading text-lg font-semibold text-secondary-800 mb-3">
                <i className="ri-check-line text-secondary-500 mr-2"></i>Best For
              </h3>
              <ul className="space-y-2">
                <li className="flex items-start gap-2 text-sm text-foreground-700">
                  <span className="text-secondary-500 mt-0.5">&#10003;</span>
                  <span>Sneaker collectors tracking multiple silhouettes and release windows</span>
                </li>
                <li className="flex items-start gap-2 text-sm text-foreground-700">
                  <span className="text-secondary-500 mt-0.5">&#10003;</span>
                  <span>Professionals refreshing their work footwear rotation</span>
                </li>
                <li className="flex items-start gap-2 text-sm text-foreground-700">
                  <span className="text-secondary-500 mt-0.5">&#10003;</span>
                  <span>Budget shoppers comparing options without algorithmic interference</span>
                </li>
                <li className="flex items-start gap-2 text-sm text-foreground-700">
                  <span className="text-secondary-500 mt-0.5">&#10003;</span>
                  <span>Stylists who need quick access to diverse footwear for client briefs</span>
                </li>
              </ul>
            </div>
            <div className="bg-accent-50 border border-accent-200/60 rounded-2xl p-6">
              <h3 className="font-heading text-lg font-semibold text-accent-700 mb-3">
                <i className="ri-information-line text-accent-500 mr-2"></i>Not Ideal For
              </h3>
              <ul className="space-y-2">
                <li className="flex items-start gap-2 text-sm text-foreground-700">
                  <span className="text-accent-400 mt-0.5">&#10007;</span>
                  <span>Shoppers who prefer browsing without any structure or categorization</span>
                </li>
                <li className="flex items-start gap-2 text-sm text-foreground-700">
                  <span className="text-accent-400 mt-0.5">&#10007;</span>
                  <span>Users expecting real-time stock-level tracking on every product link</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Getting Started */}
          <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-950 mt-12 mb-5">
            Three Steps to Shoe Discovery Mastery
          </h2>
          <div className="space-y-5 mb-8">
            <div className="flex gap-4">
              <div className="w-8 h-8 rounded-full bg-primary-500 text-background-50 flex items-center justify-center flex-shrink-0 text-sm font-semibold">1</div>
              <div>
                <h3 className="font-semibold text-foreground-900 text-sm mb-1">Open the Shoes category and browse broadly first</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">Do not filter immediately. Let your eyes scan across silhouettes. You might come looking for running shoes and discover a pair of suede Chelsea boots that perfectly fit an upcoming occasion. Broad scanning builds awareness of what is available across the entire footwear landscape.</p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="w-8 h-8 rounded-full bg-primary-500 text-background-50 flex items-center justify-center flex-shrink-0 text-sm font-semibold">2</div>
              <div>
                <h3 className="font-semibold text-foreground-900 text-sm mb-1">Cross-reference with complementary categories</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">Found a pair of formal leather Oxfords? Immediately check the Belts category for a matching leather belt. Eyeing chunky white trainers? The Bags and Accessories categories likely have coordinated pieces. The spreadsheet is designed for this exact cross-category workflow.</p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="w-8 h-8 rounded-full bg-primary-500 text-background-50 flex items-center justify-center flex-shrink-0 text-sm font-semibold">3</div>
              <div>
                <h3 className="font-semibold text-foreground-900 text-sm mb-1">Return on the weekly refresh day</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">The Shoes category updates every week, often mid-week. Make it a habit to check back around the same time each week. Consistent visitors are consistently the first to spot new drops, limited colorways, and seasonal clearance additions before they circulate on social media.</p>
              </div>
            </div>
          </div>

          {/* FAQ */}
          <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-950 mt-12 mb-5">
            Frequently Asked Questions About the Shoes Category
          </h2>
          <div className="space-y-4 mb-8">
            <div className="bg-background-100 rounded-xl p-5 border border-background-200/60">
              <h3 className="font-semibold text-foreground-900 text-sm mb-2">Q: Can I find women's and men's shoes in the same category?</h3>
              <p className="text-sm text-foreground-600 leading-relaxed">Yes. The Shoes category does not separate by gender — it organizes by silhouette and style, so unisex models appear alongside gender-specific designs. This inclusive structure reflects how modern footwear culture has increasingly moved toward unisex styling.</p>
            </div>
            <div className="bg-background-100 rounded-xl p-5 border border-background-200/60">
              <h3 className="font-semibold text-foreground-900 text-sm mb-2">Q: Does the spreadsheet include sizing information?</h3>
              <p className="text-sm text-foreground-600 leading-relaxed">The product links direct to listings that include full sizing charts and availability. The spreadsheet itself focuses on discovery and navigation rather than duplicating product-level detail that already exists on the main platform.</p>
            </div>
            <div className="bg-background-100 rounded-xl p-5 border border-background-200/60">
              <h3 className="font-semibold text-foreground-900 text-sm mb-2">Q: How do I request a specific shoe brand or model be added?</h3>
              <p className="text-sm text-foreground-600 leading-relaxed">While there is no formal request system, the weekly curation process incorporates community signals — trending searches, frequently clicked links, and category feedback all influence which products appear in future updates. The best way to see more of what you want is to engage consistently with the category.</p>
            </div>
            <div className="bg-background-100 rounded-xl p-5 border border-background-200/60">
              <h3 className="font-semibold text-foreground-900 text-sm mb-2">Q: Are the shoe links in the spreadsheet affiliate links?</h3>
              <p className="text-sm text-foreground-600 leading-relaxed">No. The Superbuy Spreadsheet links directly to product collection pages on the main platform without any affiliate tracking, sponsored placement, or commission structure. The curation is driven purely by product relevance and category organization quality.</p>
            </div>
            <div className="bg-background-100 rounded-xl p-5 border border-background-200/60">
              <h3 className="font-semibold text-foreground-900 text-sm mb-2">Q: What should I do if a shoe link stops working?</h3>
              <p className="text-sm text-foreground-600 leading-relaxed">Product links are verified weekly, but occasionally a listing may be removed between update cycles. In these cases, simply return to the <a href="/" className="text-secondary-600 hover:text-secondary-700 underline">Superbuy Spreadsheet homepage</a> and re-enter the Shoes category — the link will have been refreshed or replaced with an active alternative by the next update.</p>
            </div>
          </div>

          {/* Internal navigation */}
          <div className="mt-14 pt-8 border-t border-background-200/60">
            <div className="flex flex-col gap-4">
              <p className="text-xs font-semibold tracking-widest uppercase text-foreground-400">Continue Reading</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <a href="/superbuy-spreadsheet-guide" className="group flex items-center gap-3 bg-background-100 rounded-xl p-4 border border-background-200/60 hover:border-background-300/80 transition-all cursor-pointer">
                  <i className="ri-book-open-line text-secondary-500 text-lg"></i>
                  <div>
                    <p className="text-sm font-medium text-foreground-900">Complete Guide</p>
                    <p className="text-xs text-foreground-500">Master the entire platform</p>
                  </div>
                  <i className="ri-arrow-right-line text-foreground-400 ml-auto group-hover:translate-x-1 transition-transform"></i>
                </a>
                <a href="/comparison" className="group flex items-center gap-3 bg-background-100 rounded-xl p-4 border border-background-200/60 hover:border-background-300/80 transition-all cursor-pointer">
                  <i className="ri-scales-3-line text-secondary-500 text-lg"></i>
                  <div>
                    <p className="text-sm font-medium text-foreground-900">Comparison Guide</p>
                    <p className="text-xs text-foreground-500">Spreadsheet vs alternatives</p>
                  </div>
                  <i className="ri-arrow-right-line text-foreground-400 ml-auto group-hover:translate-x-1 transition-transform"></i>
                </a>
                <a href="/" className="group flex items-center gap-3 bg-background-100 rounded-xl p-4 border border-background-200/60 hover:border-background-300/80 transition-all cursor-pointer">
                  <i className="ri-home-4-line text-secondary-500 text-lg"></i>
                  <div>
                    <p className="text-sm font-medium text-foreground-900">Back to Home</p>
                    <p className="text-xs text-foreground-500">Browse all categories</p>
                  </div>
                  <i className="ri-arrow-right-line text-foreground-400 ml-auto group-hover:translate-x-1 transition-transform"></i>
                </a>
              </div>
            </div>
          </div>
        </div>
      </article>

      {/* Footer */}
      <footer className="bg-secondary-900 py-8 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
            <a href="/" className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-full bg-secondary-500 flex items-center justify-center">
                <i className="ri-store-2-line text-background-50 text-xs"></i>
              </div>
              <span className="font-heading text-sm font-semibold text-background-50 whitespace-nowrap">SuperBuySpread</span>
            </a>
            <p className="text-xs text-secondary-500">
              &copy; 2026 SuperBuySpread. <a href="/" className="hover:text-secondary-300 transition-colors">Home</a> &bull; <a href="/superbuy-spreadsheet-guide" className="hover:text-secondary-300 transition-colors">Guide</a> &bull; <a href="/superbuy-spreadsheet-shoes-guide" className="hover:text-secondary-300 transition-colors">Shoes Guide</a>
            </p>
          </div>
        </div>
      </footer>
    </main>
  );
}